'use client';

import { faCalendar, faCreditCard, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { usePathname } from 'next/navigation';

import { DetailsCard } from '@/components/molecules/DetailsCard';
import { DetailsPageCardsSkeleton } from '@/components/molecules/skeletons/DetailsPageCardsSkeleton';
import { currencyFormats } from '@/entities/CurrencyFormat';
import { useCurrenciesQuery } from '@/hooks/lookups/useCurrencyQuery';
import { useTeamLeagueDetailsQuery } from '@/hooks/team-leagues/useDetailsQuery';
import { useSeasonInfoQuery } from '@/hooks/teamLeagueSeasonInfo/useSeasoninfoQuery';
import { determinePriceDisplay } from '@/utils/utils';

type Props = { teamLeagueId: string };

export const TeamLeagueDetailsCards = ({ teamLeagueId }: Props) => {
	const pathname = usePathname();
	const isRoot = pathname.split('/').filter(Boolean).length === 2;

	const { data: seasonInfo, isFetching } = useSeasonInfoQuery(teamLeagueId);
	const { data: leagueInfo, isFetching: isFetchingLeagueInfo } = useTeamLeagueDetailsQuery(teamLeagueId);
	const { data: currencies, isFetching: isFetchingCurrecnies } = useCurrenciesQuery(Boolean(seasonInfo?.data && leagueInfo?.data));
	const currency = currencies?.data?.find((currency) => currency.id === leagueInfo?.data?.currencyId.toString());

	const data = seasonInfo?.data;

	const startFormattedDate = data?.registrationDateOpen && dayjs(data.registrationDateOpen).format('MMM D, YYYY');
	const startFormattedTime = data?.registrationDateOpen && dayjs(data.registrationDateOpen).format('hh:mm A');

	const endFormattedDate = data?.registrationDateClose && dayjs(data.registrationDateClose).format('MMM D, YYYY');
	const endFormattedTime = data?.registrationDateClose && dayjs(data.registrationDateClose).format('hh:mm A');

	// const showLocation = data?.teamLeagueLocation.length === 1;
	const firstLocation = data?.teamLeagueLocation.find(({ isPrimary }) => !!isPrimary) ?? data?.teamLeagueLocation?.[0];

	if (!isRoot) return null;

	const isFetchingData = isFetching || isFetchingLeagueInfo || isFetchingCurrecnies;
	if (isFetchingData) return <DetailsPageCardsSkeleton />;
	return (
		<div className="my-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
			{!leagueInfo?.data?.hideAllRegistrationInfo && (
				<>
					<DetailsCard
						title="When"
						titleIcon={<FontAwesomeIcon icon={faCalendar} />}
						customTitle
						content={
							<div className="flex w-full flex-row gap-2">
								<div className="w-1/2 text-gray-900">
									<div className="mb-4 font-bold">Registration opens</div>
									{data?.registrationDateOpen ? (
										<>
											<div>{startFormattedDate}</div>
											<div className="font-medium text-gray-500">{startFormattedTime}</div>
										</>
									) : (
										<span>TBD</span>
									)}
								</div>
								<div className="w-1/2 text-gray-900">
									<div className="mb-4 font-bold">Registration ends</div>
									{data?.registrationDateClose ? (
										<>
											<div>{endFormattedDate}</div>
											<div className="font-medium text-gray-500">{endFormattedTime}</div>
										</>
									) : (
										<span>TBD</span>
									)}
								</div>
							</div>
						}
					/>
					<DetailsCard
						title="Registration Cost"
						customTitle
						titleIcon={<FontAwesomeIcon icon={faCreditCard} />}
						content={
							<div className="flex w-full flex-col items-center gap-2 ">
								<div className={'text-4xl font-medium text-darkorange-600'}>
									{determinePriceDisplay((seasonInfo?.data?.cost || 0) / 100, currencyFormats[currency?.abbreviation || 'USD'])}
								</div>
							</div>
						}
					/>
				</>
			)}
			<DetailsCard
				title="Where"
				titleIcon={<FontAwesomeIcon icon={faLocationDot} />}
				customTitle
				subtitle={
					<div className="flex justify-end">
						<a
							target="_blank"
							href={`https://www.google.com/maps?q=${firstLocation?.venue}, ${firstLocation?.streetAddress}, ${firstLocation?.zip}`}
							aria-label="Link to get directions (opens in a new tab)"
							rel="noreferrer"
						>
							<div className="font-medium text-gray-600 underline">Get Directions</div>
						</a>
					</div>
				}
				content={
					<div className="flex w-full flex-col gap-2 text-gray-900">
						<div className="font-bold">{firstLocation?.venue}</div>
						<div>{firstLocation?.streetAddress}</div>
						<div className="font-medium text-gray-900">
							{[firstLocation?.city, firstLocation?.countryInfo.abbreviation, firstLocation?.zip].join(', ')}
						</div>
					</div>
				}
			/>
		</div>
	);
};
